import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import type { GpsTrackingPoint } from "../model/types";

/**
 * Определяет цвет статуса машины на основе активности
 */
export function getVehicleStatusColor(vehicle: ConnectProto.Truck): string {
  const now = new Date();
  const updatedAt = fromTimestamp(vehicle.updatedAt);
  if (!updatedAt) return "bg-brand-red";

  const diffInMs = now.getTime() - updatedAt.getTime();
  const diffInMinutes = diffInMs / (1000 * 60);
  const diffInHours = diffInMs / (1000 * 60 * 60);

  if (diffInMinutes < 30) {
    return "bg-brand-green";
  } else if (diffInHours < 24) {
    return "bg-accent-yellow";
  } else {
    return "bg-brand-red";
  }
}

/** Полное имя водителя смены; пустая строка, если водителя нет. */
export function getDriverName(shift: ConnectProto.DriverOnShift): string {
  return `${shift.driver?.firstName || ""} ${shift.driver?.lastName || ""}`.trim();
}

/** A started shift is played back from its actual start to its end, or up to now. */
export function getShiftWindow(shift: ConnectProto.DriverOnShift) {
  return { from: fromTimestamp(shift.startedAt), to: fromTimestamp(shift.completedAt) };
}

/**
 * Получает последнюю позицию из массива GPS точек по vehicleId
 */
export function getLastPositionByVehicleId<T extends { vehicleId: string; timestamp: Date }>(
  points: T[],
  vehicleId: string,
): T | undefined {
  const vehiclePoints = points.filter((p) => p.vehicleId === vehicleId);
  if (vehiclePoints.length === 0) return undefined;

  return vehiclePoints.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
}

/** GetTruckTravelPath's `geojsonFeature`: a LineString + parallel per-point arrays. */
type TravelPathFeature = {
  geometry: { coordinates: number[][] };
  properties: { timestamps?: number[]; speeds?: number[]; angles?: number[] };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

// The feature arrives as an untyped `google.protobuf.Struct`, so its shape is checked here.
const isTravelPathFeature = (feature: unknown): feature is TravelPathFeature =>
  isRecord(feature) &&
  isRecord(feature.geometry) &&
  Array.isArray(feature.geometry.coordinates) &&
  feature.geometry.coordinates.every(Array.isArray) &&
  isRecord(feature.properties);

/**
 * Turns a truck's travel path (GeoJSON LineString + parallel timestamp / speed /
 * angle arrays, as returned by GetTruckTravelPath) into GPS points. Returns []
 * for a missing or malformed feature.
 */
export function travelPathToGpsPoints(
  feature: unknown,
  vehicle: { id: bigint; plateNumber: string },
  driverNames: string[],
): GpsTrackingPoint[] {
  if (!isTravelPathFeature(feature)) return [];
  const { timestamps, speeds, angles } = feature.properties;
  return feature.geometry.coordinates.map((coord, i) => ({
    id: `point-${vehicle.id}-${i}`,
    vehicleId: String(vehicle.id),
    plateNumber: vehicle.plateNumber,
    driverNames,
    timestamp: new Date((timestamps?.[i] || 0) * 1000),
    lat: coord[1],
    lon: coord[0],
    speed: speeds?.[i] || 0,
    angle: angles?.[i] || 0,
  }));
}

/** A gap longer than this between two track points means the tracker was off. */
const ENGINE_GAP_MS = 5 * 60_000;
/** A hop implying more than this is GPS noise, not driving. */
const MAX_PLAUSIBLE_SPEED_KMH = 150;

const haversineKm = (a: GpsTrackingPoint, b: GpsTrackingPoint) => {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLon = (b.lon - a.lon) * rad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
};

/**
 * Mileage and engine hours of a track. Mileage sums the hops between points,
 * dropping implausible jumps. The travel path carries no ignition data, so engine
 * hours are approximated as the time the tracker kept reporting — consecutive
 * points no more than ENGINE_GAP_MS apart.
 */
export function computeTrackStats(points: GpsTrackingPoint[]) {
  let mileageKm = 0;
  let engineMs = 0;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const next = points[i];
    const dtMs = next.timestamp.getTime() - prev.timestamp.getTime();
    if (dtMs <= 0) continue;
    const km = haversineKm(prev, next);
    if (km / (dtMs / 3_600_000) <= MAX_PLAUSIBLE_SPEED_KMH) mileageKm += km;
    if (dtMs <= ENGINE_GAP_MS) engineMs += dtMs;
  }
  return {
    mileageKm: Math.round(mileageKm * 10) / 10,
    engineHours: engineMs / 3_600_000,
  };
}
